import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {
  Pagination,
  Stack,
  TablePagination,
  TableSortLabel,
} from "@mui/material";
import { Box } from "@mui/system";
import Link from "@mui/material/Link";
import { useNavigate, useSearchParams } from "react-router-dom";
import BrandInterfaceModal from "./BrandInterfaceModal/BrandInterfaceModal";

interface Props {
  tableHeadings: { [key: string]: string };
  rows: any[];
  brandStepCoverage: any[];
}

type SortDirection = "asc" | "desc";

const hasValue = (value: any) =>
  value !== null && value !== undefined && value !== "";

const ByInterfaceTable = ({
  tableHeadings,
  rows,
  brandStepCoverage,
}: Props) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get("page") ?? 0);
  const rowsPerPage = Number(searchParams.get("rowsPerPage") ?? 10);
  const [sortKey, setSortKey] = React.useState<string | null>(null);
  const [sortDirection, setSortDirection] =
    React.useState<SortDirection>("asc");
  const headCells = Object.entries(tableHeadings);

  const handleChangePage = (_: any, newPage: number) => {
    const next = new URLSearchParams(searchParams);
    if (newPage === 0) {
      next.delete("page");
    } else {
      next.set("page", String(newPage));
    }
    setSearchParams(next, { replace: true });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const next = new URLSearchParams(searchParams);
    const value = +event.target.value;
    if (value === 10) {
      next.delete("rowsPerPage");
    } else {
      next.set("rowsPerPage", String(value));
    }
    next.delete("page");
    setSearchParams(next, { replace: true });
  };

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };

  const sortedRows = React.useMemo(() => {
    if (!sortKey) return rows;
    return [...rows].sort((a, b) => {
      const aValue = a[sortKey];
      const bValue = b[sortKey];
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      const comparison =
        typeof aValue === "number" && typeof bValue === "number"
          ? aValue - bValue
          : String(aValue).localeCompare(String(bValue));
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [rows, sortKey, sortDirection]);

  const [brandsModalRow, setBrandsModalRow] = React.useState<any>(null);

  const brandsModalRows = React.useMemo(() => {
    if (!brandsModalRow) return [];
    return brandStepCoverage
      .filter(
        (row: any) =>
          row.backend_interface_id === brandsModalRow.backend_interface_id,
      )
      .map((row: any) => ({
        brand_id: row.brand_id,
        brand_name: row.brand_name,
      }));
  }, [brandStepCoverage, brandsModalRow]);

  return (
    <>
      <TableContainer className="">
        <Table size="small" stickyHeader sx={{ whiteSpace: "nowrap" }}>
          <TableHead>
            <TableRow>
              {headCells.map(([key, value], index) => (
                <TableCell key={index} align="left">
                  <TableSortLabel
                    active={sortKey === key}
                    direction={sortKey === key ? sortDirection : "asc"}
                    onClick={() => handleSort(key)}
                  >
                    {value && typeof value === "string" ? value : ""}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row) => (
                <React.Fragment key={row.backend_interface_id}>
                  <TableRow
                    sx={{
                      "& > *": {
                        borderBottom:
                          "1px solid rgba(224, 224, 224, 1) !important",
                      },
                    }}
                  >
                    {headCells.map(([key], cellIndex) => (
                      <TableCell key={cellIndex} align="left">
                        {key === "interface" ? (
                          hasValue(row[key]) ? (
                            <Link
                              component="button"
                              data-tracker="983"
                              onClick={() =>
                                navigate(
                                  `/data_collection/families/interface?interfaceId=${row.backend_interface_id}&interfaceName=${encodeURIComponent(row.interface ?? "")}`,
                                )
                              }
                              underline="always"
                              color="secondary"
                            >
                              {row[key]}
                            </Link>
                          ) : (
                            ""
                          )
                        ) : key === "brands" ? (
                          !hasValue(row[key]) ? (
                            ""
                          ) : row[key] > 0 ? (
                            <Link
                              component="button"
                              data-tracker="982"
                              onClick={() => setBrandsModalRow(row)}
                              underline="always"
                              color="secondary"
                            >
                              {row[key]}
                            </Link>
                          ) : (
                            row[key]
                          )
                        ) : key === "fetcher" ? (
                          hasValue(row[key]) ? (
                            <Box display={"flex"} alignItems={"center"}>
                              <span
                                style={{
                                  width: 12,
                                  height: 12,
                                  backgroundColor: "#607D8B",
                                  borderRadius: "2px",
                                  display: "inline-flex",
                                  marginRight: "5px",
                                }}
                              ></span>
                              {row[key]}
                            </Box>
                          ) : (
                            ""
                          )
                        ) : key === "unifier" ? (
                          hasValue(row[key]) ? (
                            <Box display={"flex"} alignItems={"center"}>
                              <span
                                style={{
                                  width: 12,
                                  height: 12,
                                  backgroundColor: "#E91E63",
                                  borderRadius: "2px",
                                  display: "inline-flex",
                                  marginRight: "5px",
                                }}
                              ></span>
                              {row[key]}
                            </Box>
                          ) : (
                            ""
                          )
                        ) : key === "advisor" ? (
                          hasValue(row[key]) ? (
                            <Box display={"flex"} alignItems={"center"}>
                              <span
                                style={{
                                  width: 12,
                                  height: 12,
                                  backgroundColor: "#2196F3",
                                  borderRadius: "2px",
                                  display: "inline-flex",
                                  marginRight: "5px",
                                }}
                              ></span>
                              {row[key]}
                            </Box>
                          ) : (
                            ""
                          )
                        ) : hasValue(row[key]) ? (
                          row[key]
                        ) : (
                          ""
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </React.Fragment>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      {brandsModalRow && (
        <BrandInterfaceModal
          open={!!brandsModalRow}
          handleClose={() => setBrandsModalRow(null)}
          title={`${brandsModalRow.interface} Interface`}
          brands={brandsModalRows}
          brandsCount={brandsModalRow.brands}
          subtitleSuffix="Brands use this Interface"
        />
      )}
      <Stack
        spacing={1}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={rows?.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          classes={{
            displayedRows: "hidden-displayed-rows", // Apply the custom class
          }}
          sx={{
            ".MuiTablePagination-actions": {
              display: "none",
            },
            ".MuiTablePagination-input": {
              marginLeft: "15px",
            },
            ".MuiTablePagination-toolbar": {
              paddingLeft: "16px",
            },
          }}
        />
        <Pagination
          count={Math.ceil(rows?.length / rowsPerPage)}
          page={page + 1}
          showFirstButton
          showLastButton
          className="pagination-button"
          color="secondary"
          size="small"
          sx={{
            ".css-wjh20t-MuiPagination-ul": {
              justifyContent: "center",
            },
          }}
          onChange={(_, value) => handleChangePage(null, value - 1)}
        />
        <TablePagination
          rowsPerPageOptions={[]}
          component="div"
          count={rows?.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            ".css-16c50h-MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
              {
                display: "none",
              },
            ".MuiTablePagination-actions": {
              display: "none",
            },
            ".MuiTablePagination-toolbar": {
              paddingRight: "16px",
            },
          }}
        />
      </Stack>
    </>
  );
};

export default ByInterfaceTable;
